import { useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useExternalProviderRepository } from "../context/external-provider-repository.context";
import { useToast } from "@components/ui/use-toast";
import type { AppState } from "@redux/app-state";
import type { RequestState } from "@redux/state/shared-types";
import type { SubmitRequestDataBody } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import { uploadSpeakerResume } from "@academy-context/write/application/use-cases/external-provider/speaker-resume-upload/upload-speaker-resume";
import {
  submitRequestData,
  resetSubmitRequestData,
} from "@academy-context/write/application/use-cases/external-provider/request-data-submission/submit-request-data";

const selectExternalProviderSpeakerResumeUpload = (state: AppState) => state.externalProvider.speakerResumeUpload;
const selectExternalProviderRequestDataSubmission = (state: AppState) => state.externalProvider.requestDataSubmission;

export interface UseRequestDataSubmitResult {
  uploadSpeakerResume: (file: File) => Promise<string | null>;
  submitRequestData: (requestId: string, body: SubmitRequestDataBody) => Promise<boolean>;
  uploadProcessing: RequestState;
  submitProcessing: RequestState;
  submitSuccess: boolean;
}

export const useRequestDataSubmit = (onSuccess: () => void): UseRequestDataSubmitResult => {
  const dispatch = useAppDispatch();
  const repository = useExternalProviderRepository();
  const { toast } = useToast();

  const { processing: uploadProcessing } = useAppSelector(selectExternalProviderSpeakerResumeUpload);
  const { processing: submitProcessing } = useAppSelector(selectExternalProviderRequestDataSubmission);

  useEffect(() => {
    if (submitProcessing === "success") {
      toast({ description: "Données soumises avec succès ! En attente de validation admin." });
      onSuccess();
      dispatch(resetSubmitRequestData());
    } else if (submitProcessing === "failed") {
      toast({
        description: "Erreur lors de la soumission des données",
        variant: "destructive",
      });
      dispatch(resetSubmitRequestData());
    }
  }, [dispatch, submitProcessing, toast, onSuccess]);

  const handleUploadSpeakerResume = useCallback(
    async (file: File): Promise<string | null> => {
      try {
        const url = await dispatch(uploadSpeakerResume({ file, externalProviderRepository: repository })).unwrap();
        toast({ description: "CV téléchargé avec succès" });
        return url;
      } catch {
        toast({
          description: "Erreur lors du téléchargement du CV",
          variant: "destructive",
        });
        return null;
      }
    },
    [dispatch, repository, toast],
  );

  const handleSubmitRequestData = useCallback(
    async (requestId: string, body: SubmitRequestDataBody): Promise<boolean> => {
      try {
        await dispatch(submitRequestData({ requestId, body, externalProviderRepository: repository })).unwrap();
        return true;
      } catch {
        return false;
      }
    },
    [dispatch, repository],
  );

  return {
    uploadSpeakerResume: handleUploadSpeakerResume,
    submitRequestData: handleSubmitRequestData,
    uploadProcessing,
    submitProcessing,
    submitSuccess: submitProcessing === "success",
  };
};
